import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminStats } from "@/hooks/useAdminStats"
import { useUserMap } from "@/hooks/useUserMap"
import { Users, Activity, ShieldAlert, AlertTriangle } from "lucide-react"

export default function AdminDashboard() {
  const { stats, loading, error } = useAdminStats()
  const { resolveEmail } = useUserMap()

  if (error) {
    return (
      <div className="text-center py-10 text-destructive">
        <p>Failed to load stats: {error}</p>
      </div>
    )
  }

  const cards = [
    { title: "アクティブユーザー", value: stats?.activeUsers ?? 0, icon: Users },
    { title: "リクエスト（24時間）", value: stats?.totalRequests ?? 0, icon: Activity },
    {
      title: "ガードレールブロック",
      value: stats?.guardrailBlocks ?? 0,
      icon: ShieldAlert,
    },
    { title: "エラー", value: stats?.errorCount ?? 0, icon: AlertTriangle },
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">ダッシュボード</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{card.value}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Alerts Table */}
      <Card>
        <CardHeader>
          <CardTitle>最近のアラート</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !stats?.recentAlerts?.length ? (
            <p className="text-muted-foreground text-center py-4">アラートはありません</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">日時</th>
                    <th className="text-left py-2 px-3">種類</th>
                    <th className="text-left py-2 px-3">ユーザー</th>
                    <th className="text-left py-2 px-3">詳細</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentAlerts.map((event, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-2 px-3">
                        {new Date(event.timestamp).toLocaleString()}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            event.eventType === "error"
                              ? "bg-destructive/10 text-destructive"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {event.eventType}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="text-xs">{resolveEmail(event.userId) || "-"}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {event.userId?.slice(0, 8)}...
                        </div>
                      </td>
                      <td className="py-2 px-3 truncate max-w-xs">
                        {event.outputSummary || event.toolName || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
